import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Mail, ArrowRight, LogOut, RotateCcw, Loader2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CONTACT_EMAIL = "support@pawbookhq.com";

/**
 * Sticky top banner shown only when the current user has ``is_demo: true``.
 * Provides the two conversion CTAs the user asked for, a one-tap reset of
 * the sandbox, and a clean exit path.
 */
export default function DemoModeBanner({ onReset }) {
  const { user, logout } = useAuth();
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!user || !user.is_demo) return null;

  async function handleReset() {
    if (resetting) return;
    setResetting(true);
    try {
      await api.post("/demo/reset");
      toast.success("Demo data reset — fresh sandbox loaded", {
        description: "All sample dogs, sessions, and payments are back to defaults.",
      });
      if (typeof onReset === "function") {
        await onReset();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reset demo data");
    } finally {
      setResetting(false);
    }
  }

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      toast.success("Email copied", { description: CONTACT_EMAIL });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.message("Press and hold to copy:", { description: CONTACT_EMAIL });
    }
  }

  return (
    <div
      data-testid="demo-mode-banner"
      className="sticky top-0 z-40 border-b border-amber-300/60 bg-amber-50/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2.5 md:px-8">
        <div className="flex items-center gap-2 text-sm text-amber-900">
          <Sparkles size={16} className="text-amber-600" />
          <span className="font-semibold">Demo Mode</span>
          <span className="hidden text-amber-800/80 sm:inline">
            · Exploring a sandbox with sample data — destructive actions are disabled
            and data resets on each visit.
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="demo-banner-reset"
            size="sm"
            variant="outline"
            disabled={resetting}
            onClick={handleReset}
            className="rounded-full border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
            title="Wipe and re-seed the demo workspace"
          >
            {resetting ? (
              <Loader2 size={13} className="mr-1.5 animate-spin" />
            ) : (
              <RotateCcw size={13} className="mr-1.5" />
            )}
            {resetting ? "Resetting…" : "Reset demo data"}
          </Button>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Book%20a%20setup%20call`}
            data-testid="demo-banner-book-call"
          >
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
            >
              <Mail size={13} className="mr-1.5" />
              Book a setup call
            </Button>
          </a>
          <Button
            data-testid="demo-banner-copy-email"
            size="sm"
            variant="outline"
            onClick={copyEmail}
            className="rounded-full border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
            title={`Copy ${CONTACT_EMAIL} — works without a mail client`}
          >
            {copied ? (
              <Check size={13} className="mr-1.5 text-emerald-600" />
            ) : (
              <Copy size={13} className="mr-1.5" />
            )}
            <span className="hidden sm:inline">{copied ? "Copied" : "Copy email"}</span>
            <span className="sm:hidden">{copied ? "Copied" : "Copy"}</span>
          </Button>
          <Link to="/login" data-testid="demo-banner-start-account">
            <Button
              size="sm"
              className="rounded-full bg-amber-600 text-white hover:bg-amber-700"
              onClick={() => logout()}
            >
              Start your own account
              <ArrowRight size={13} className="ml-1" />
            </Button>
          </Link>
          <Link to="/" data-testid="demo-banner-home" title="Back to Pawbook home">
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full text-amber-900 hover:bg-amber-100"
              onClick={() => logout()}
            >
              <LogOut size={13} className="sm:mr-1.5" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
